import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Trash2, RefreshCw, Mail, Phone, MapPin, FileText, Clock, Lock, Eye, EyeOff, DollarSign, Home, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const token = sessionStorage.getItem("adminToken");
    if (token) {
      setIsAuthenticated(true);
      fetchLeads();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        sessionStorage.setItem("adminToken", response.data.token);
        setIsAuthenticated(true);
        fetchLeads();
        toast.success("Login successful");
      }
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    setIsAuthenticated(false);
    setLeads([]);
    toast.success("Logged out");
  };

  const exportToCSV = () => {
    if (leads.length === 0) {
      toast.error("No leads to export");
      return;
    }

    // CSV headers
    const headers = ["Name", "Phone", "Email", "Property Address", "Condition", "Asking Price", "Notes", "Date", "Status"];
    
    // CSV rows
    const rows = leads.map(lead => [
      lead.full_name,
      lead.phone,
      lead.email,
      lead.property_address,
      lead.property_condition || "",
      lead.asking_price || "",
      lead.notes || "",
      formatDate(lead.created_at),
      lead.status
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `jjbuyshouses-leads-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Leads exported successfully");
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status?status=${status}`);
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status } : lead
      ));
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      setLeads(leads.filter(lead => lead.id !== leadId));
      toast.success("Lead deleted");
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-blue-100 text-blue-700 hover:bg-blue-100",
      contacted: "bg-yellow-100 text-yellow-700 hover:bg-yellow-100",
      qualified: "bg-green-100 text-green-700 hover:bg-green-100",
      closed: "bg-slate-100 text-slate-700 hover:bg-slate-100",
    };
    return styles[status] || styles.new;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 bg-[#D32F2F] rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>JJ</span>
                </div>
                <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  buyshouses
                </span>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-slate-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Admin Login
              </h1>
              <p className="text-slate-500 mt-2">Enter your password to access leads</p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="password" className="text-slate-700 font-medium">
                    Password
                  </Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter admin password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pl-10 pr-10 h-12 border-slate-200 focus:border-[#D32F2F] focus:ring-[#D32F2F]/20"
                      data-testid="admin-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full h-12 bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold"
                  data-testid="admin-login-btn"
                >
                  {loginLoading ? "Logging in..." : "Login"}
                </Button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
                ← Back to Site
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="back-to-home-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Site
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Lead Management
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={exportToCSV}
                variant="outline" 
                size="sm"
                disabled={leads.length === 0}
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button 
                onClick={fetchLeads} 
                variant="outline" 
                size="sm"
                disabled={loading}
                data-testid="refresh-leads-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={handleLogout} 
                variant="ghost" 
                size="sm"
                className="text-slate-500 hover:text-slate-700"
                data-testid="logout-btn"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card data-testid="total-leads-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Total Leads</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-slate-900">{leads.length}</p>
            </CardContent>
          </Card>
          <Card data-testid="new-leads-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">New</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">
                {leads.filter(l => l.status === 'new').length}
              </p>
            </CardContent>
          </Card>
          <Card data-testid="contacted-leads-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Contacted</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-yellow-600">
                {leads.filter(l => l.status === 'contacted').length}
              </p>
            </CardContent>
          </Card>
          <Card data-testid="qualified-leads-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Qualified</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">
                {leads.filter(l => l.status === 'qualified').length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Leads Table */}
        <Card data-testid="leads-table-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">All Leads</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-12" data-testid="no-leads-message">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No leads yet. They will appear here once submitted.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="leads-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Property Address</TableHead>
                      <TableHead>Condition</TableHead>
                      <TableHead>Asking Price</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                        <TableCell className="font-medium">{lead.full_name}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3 text-slate-400" />
                              <a href={`tel:${lead.phone}`} className="hover:text-primary">
                                {lead.phone}
                              </a>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="w-3 h-3 text-slate-400" />
                              <a href={`mailto:${lead.email}`} className="hover:text-primary">
                                {lead.email}
                              </a>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-start gap-1">
                            <MapPin className="w-3 h-3 text-slate-400 mt-1 flex-shrink-0" />
                            <span className="text-sm">{lead.property_address}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">
                            {lead.property_condition || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium text-slate-700">
                            {lead.asking_price ? `$${lead.asking_price}` : "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500 line-clamp-2">
                            {lead.notes || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Clock className="w-3 h-3" />
                            {formatDate(lead.created_at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={lead.status}
                            onValueChange={(value) => updateStatus(lead.id, value)}
                          >
                            <SelectTrigger 
                              className="w-[120px] h-8"
                              data-testid={`status-select-${lead.id}`}
                            >
                              <SelectValue>
                                <Badge className={getStatusBadge(lead.status)}>
                                  {lead.status}
                                </Badge>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="qualified">Qualified</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteLead(lead.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminPage;
